import React, { useContext, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { AuthContext } from '../Provider';
import { useTranslation } from 'react-i18next';
import {
  ModernCard,
  CustomButton1,
  CustomButton2,
  CustomButton3,
  CustomButton4,
  CustomButton5,
  CustomButton6,
  CustomButton7,
  CustomButton8,
  CustomButton10,
  CustomButton10Ca,
  CustomButton10Co,
  CustomButton11,
  Checkbox1,
  RadioButton1,
  InputName1,
  InputPassword1,
  CustomInputBox1,
  CustomInputBox2,
  CustomTextArea,
  ModernDropdown,
  ModernSwitch,
  ModernFormSection,
  ModernDateTimePicker,
  DateTimePickerContainer,
  DatePickerWrapper,
  TimePickerWrapper,
  AddStopButton,
  NavigationContainer,
  RefreshButton,
  NavButton,
  EventCardButton,
  EventCardDetail,
  InlineThemeSelector,
  CloseButton1,
  CloseButton2,
  CloseButton3,
  SearchButton1,
  SearchButton2,
  SearchButton3,
  SearchButton4,
  ChatInput,
  BackButton1,
  BackButton2,
  BackButton3,
  BackButton4,
  Button,
  IconButton
} from '../Templates';
import CustomButton9 from '../Templates';
import Spinner from '../common/Spinner';
import { useToast } from '../hooks/ToastContext';
import { FaCar, FaUser, FaCalendarAlt, FaClock, FaPlus, FaArrowRight, FaSun, FaMoon, FaCog, FaBell, FaExclamationTriangle, FaCheckCircle, FaInfoCircle, FaChevronLeft, FaChevronRight } from 'react-icons/fa';
import './Developer.css';

// Developer user ID - only this user can access the developer page
const DEVELOPER_USER_ID = '3f3d24fd-cf1f-42c8-b12e-3cfa5948c17d';

const Developer = () => {
  const { t } = useTranslation('common');
  const { user, loading } = useContext(AuthContext);
  const navigate = useNavigate();
  const [showTemplates, setShowTemplates] = useState(false);
  const [showComponents, setShowComponents] = useState(false);
  const [currentButtonPage, setCurrentButtonPage] = useState(0);
  const [dropdownValue, setDropdownValue] = useState('');
  const [switchValue, setSwitchValue] = useState(false);
  const [inputValue, setInputValue] = useState('');
  const [passwordValue, setPasswordValue] = useState('');
  const [textareaValue, setTextareaValue] = useState('');
  const [radioValue, setRadioValue] = useState('option1');
  const [dateValue, setDateValue] = useState(new Date());
  const [timeValue, setTimeValue] = useState(new Date());
  const [refreshLoading, setRefreshLoading] = useState(false);
  const [chatMessage, setChatMessage] = useState('');
  const { addToast } = useToast();

  useEffect(() => {
    // Check if user is authorized to view this page
    if (!loading && (!user || user.id !== DEVELOPER_USER_ID)) {
      console.log('Unauthorized access to developer page');
      navigate('/');
    }
  }, [user, loading, navigate]);

  // Show loading while checking auth
  if (loading) {
    return (
      <div className="developer-loading">
        <Spinner size="large" />
      </div>
    );
  }

  // Additional security check
  if (!user || user.id !== DEVELOPER_USER_ID) {
    return null;
  }

  return (
    <div className="developer-container">
      <div className="developer-header">
        <h1>Developer Dashboard</h1>
        <p className="developer-subtitle">Welcome back, {user.email}</p>
      </div>

      <div className="developer-content">
        <div className="developer-stats-grid">
          <ModernCard className="dev-stat-card">
            <h3>Database Status</h3>
            <p className="stat-value">Connected</p>
            <p className="stat-label">Supabase Project: Active</p>
          </ModernCard>

          <ModernCard className="dev-stat-card">
            <h3>Environment</h3>
            <p className="stat-value">Development</p>
            <p className="stat-label">React {React.version}</p>
          </ModernCard>

          <ModernCard className="dev-stat-card">
            <h3>Build Info</h3>
            <p className="stat-value">v0.1.0</p>
            <p className="stat-label">Last Deploy: Today</p>
          </ModernCard>

          <ModernCard className="dev-stat-card">
            <h3>API Status</h3>
            <p className="stat-value">Operational</p>
            <p className="stat-label">All systems running</p>
          </ModernCard>
        </div>

        <div className="developer-sections">
          <ModernCard className="dev-section">
            <h2>Quick Actions</h2>
            <div className="dev-actions">
              <CustomButton2 
                label="View Database" 
                onClick={() => window.open('https://supabase.com/dashboard/project/idivlaghsxxtqhqhbmzf', '_blank')}
              />
              <CustomButton2 
                label="View Templates" 
                onClick={() => setShowTemplates(!showTemplates)}
              />
              <CustomButton2 
                label="Components" 
                onClick={() => setShowComponents(!showComponents)}
              />
              <CustomButton2 
                label="Clear Cache" 
                onClick={() => {
                  localStorage.clear();
                  sessionStorage.clear();
                  alert('Cache cleared! Page will reload.');
                  window.location.reload();
                }}
              />
              <CustomButton2 
                label="Toggle Debug Mode" 
                onClick={() => {
                  const currentDebug = localStorage.getItem('debugMode') === 'true';
                  localStorage.setItem('debugMode', !currentDebug);
                  alert(`Debug mode ${!currentDebug ? 'enabled' : 'disabled'}`);
                }}
              />
            </div>
          </ModernCard>

          <ModernCard className="dev-section">
            <h2>System Information</h2>
            <div className="system-info">
              <div className="info-row">
                <span className="info-label">User Agent:</span>
                <span className="info-value">{navigator.userAgent}</span>
              </div>
              <div className="info-row">
                <span className="info-label">Screen Resolution:</span>
                <span className="info-value">{window.screen.width} x {window.screen.height}</span>
              </div>
              <div className="info-row">
                <span className="info-label">Language:</span>
                <span className="info-value">{navigator.language}</span>
              </div>
              <div className="info-row">
                <span className="info-label">Online Status:</span>
                <span className="info-value">{navigator.onLine ? 'Online' : 'Offline'}</span>
              </div>
            </div>
          </ModernCard>

          <ModernCard className="dev-section">
            <h2>Development Tools</h2>
            <p className="dev-note">
              This section is under construction. Future features will include:
            </p>
            <ul className="feature-list">
              <li>Real-time database monitoring</li>
              <li>User activity analytics</li>
              <li>Error logging viewer</li>
              <li>Feature flag management</li>
              <li>API endpoint testing</li>
              <li>Performance metrics</li>
            </ul>
          </ModernCard>

          {/* Components Showcase Section */}
          {showComponents && (
            <ModernCard className="dev-section components-showcase">
              <h2>Components Showcase</h2>
              
              {/* Spinner Variations Section */}
              <div className="components-section">
                <h3>Loading Spinners</h3>
                <div className="components-grid spinners-grid">
                  <div className="component-item">
                    <label>Small Spinner</label>
                    <div className="spinner-demo">
                      <Spinner size="small" showText={false} />
                    </div>
                  </div>
                  
                  <div className="component-item">
                    <label>Medium Spinner</label>
                    <div className="spinner-demo">
                      <Spinner size="medium" showText={false} />
                    </div>
                  </div>
                  
                  <div className="component-item">
                    <label>Large Spinner</label>
                    <div className="spinner-demo">
                      <Spinner size="large" showText={false} />
                    </div>
                  </div>
                  
                  <div className="component-item">
                    <label>With Text</label>
                    <div className="spinner-demo">
                      <Spinner size="medium" showText={true} customText="Loading..." />
                    </div>
                  </div>
                  
                  <div className="component-item">
                    <label>Custom Color</label>
                    <div className="spinner-demo">
                      <Spinner size="medium" color="#ff6b6b" showText={false} />
                    </div>
                  </div>
                  
                  <div className="component-item">
                    <label>Thick Border</label>
                    <div className="spinner-demo">
                      <Spinner size="medium" thickness={5} showText={false} />
                    </div>
                  </div>
                  
                  <div className="component-item">
                    <label>Thin Border</label>
                    <div className="spinner-demo">
                      <Spinner size="medium" thickness={1} showText={false} />
                    </div>
                  </div>
                  
                  <div className="component-item">
                    <label>Accent Color</label>
                    <div className="spinner-demo">
                      <Spinner size="medium" color="var(--accent-color)" showText={false} />
                    </div>
                  </div>
                </div>
              </div>

              {/* Toast Notifications Section */}
              <div className="components-section">
                <h3>Toast Notifications</h3>
                <div className="components-grid toast-grid">
                  <div className="component-item">
                    <label>Success Toast</label>
                    <CustomButton2 
                      label="Show Success" 
                      onClick={() => addToast({
                        type: 'success',
                        title: 'Success!',
                        description: 'Your action was completed successfully.'
                      })}
                    />
                  </div>
                  
                  <div className="component-item">
                    <label>Error Toast</label>
                    <CustomButton2 
                      label="Show Error" 
                      onClick={() => addToast({
                        type: 'error',
                        title: 'Error Occurred',
                        description: 'Something went wrong. Please try again.'
                      })}
                    />
                  </div>
                  
                  <div className="component-item">
                    <label>Warning Toast</label>
                    <CustomButton2 
                      label="Show Warning" 
                      onClick={() => addToast({
                        type: 'warning',
                        title: 'Warning',
                        description: 'Please check your input before proceeding.'
                      })}
                    />
                  </div>
                  
                  <div className="component-item">
                    <label>Info Toast</label>
                    <CustomButton2 
                      label="Show Info" 
                      onClick={() => addToast({
                        type: 'info',
                        title: 'Information',
                        description: 'Here\'s some useful information for you.'
                      })}
                    />
                  </div>
                  
                  <div className="component-item">
                    <label>Short Message</label>
                    <CustomButton2 
                      label="Show Short" 
                      onClick={() => addToast({
                        type: 'success',
                        title: 'Done!'
                      })}
                    />
                  </div>
                  
                  <div className="component-item">
                    <label>Long Message</label>
                    <CustomButton2 
                      label="Show Long" 
                      onClick={() => addToast({
                        type: 'info',
                        title: 'Detailed Information',
                        description: 'This is a longer message that demonstrates how the toast component handles multiple lines of text and wraps them properly within the notification container.'
                      })}
                    />
                  </div>
                </div>
              </div>

              {/* Loading States Section */}
              <div className="components-section">
                <h3>Loading States Examples</h3>
                <div className="components-grid loading-states-grid">
                  <div className="component-item">
                    <label>Button Loading</label>
                    <CustomButton4 
                      label="Processing..." 
                      disabled={true}
                      onClick={() => {}}
                    />
                  </div>
                  
                  <div className="component-item">
                    <label>Form Loading</label>
                    <div className="form-loading-demo">
                      <CustomInputBox2 placeholder="Loading..." disabled />
                      <Spinner size="small" showText={false} />
                    </div>
                  </div>
                  
                  <div className="component-item wide">
                    <label>Card Loading</label>
                    <ModernCard className="loading-card-demo">
                      <div className="loading-content">
                        <Spinner size="medium" customText="Loading content..." />
                      </div>
                    </ModernCard>
                  </div>
                </div>
              </div>

              {/* Interactive Demo Section */}
              <div className="components-section">
                <h3>Interactive Demo</h3>
                <div className="interactive-demo">
                  <div className="demo-controls">
                    <CustomButton2 
                      label="Trigger Multiple Toasts" 
                      onClick={() => {
                        addToast({ type: 'info', title: 'Starting process...', description: 'Initializing system' });
                        setTimeout(() => {
                          addToast({ type: 'warning', title: 'Processing...', description: 'This may take a moment' });
                        }, 1000);
                        setTimeout(() => {
                          addToast({ type: 'success', title: 'Complete!', description: 'All operations finished successfully' });
                        }, 3000);
                      }}
                    />
                    
                    <CustomButton2 
                      label="Test Error Handling" 
                      onClick={() => {
                        addToast({ 
                          type: 'error', 
                          title: 'Connection Failed', 
                          description: 'Unable to connect to server. Check your internet connection and try again.' 
                        });
                      }}
                    />
                  </div>
                  
                  <div className="demo-info">
                    <p><strong>Features Demonstrated:</strong></p>
                    <ul>
                      <li>Multiple spinner sizes and configurations</li>
                      <li>All toast notification types</li>
                      <li>Loading states for different components</li>
                      <li>Responsive design and themes</li>
                      <li>Auto-dismiss and manual close functionality</li>
                    </ul>
                  </div>
                </div>
              </div>
            </ModernCard>
          )}

          {/* Templates Preview Section */}
          {showTemplates && (
            <ModernCard className="dev-section templates-preview">
              <h2>UI Component Templates</h2>
              
              {/* New Button System (redesign) */}
              <div className="templates-section">
                <h3>New Button System (redesign)</h3>
                <div className="templates-grid buttons-grid">
                  <div className="template-item">
                    <label>Button · primary</label>
                    <Button variant="primary" onClick={() => {}}>Publicēt</Button>
                  </div>
                  <div className="template-item">
                    <label>Button · secondary</label>
                    <Button variant="secondary" onClick={() => {}}>Atcelt</Button>
                  </div>
                  <div className="template-item">
                    <label>Button · tertiary</label>
                    <Button variant="tertiary" onClick={() => {}}>Aizmirsi paroli?</Button>
                  </div>
                  <div className="template-item">
                    <label>Button · destructive</label>
                    <Button variant="destructive" onClick={() => {}}>Dzēst kontu</Button>
                  </div>
                  <div className="template-item">
                    <label>Button · sizes</label>
                    <div style={{ display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap' }}>
                      <Button variant="primary" size="sm">Sm</Button>
                      <Button variant="primary" size="md">Md</Button>
                      <Button variant="primary" size="lg">Lg</Button>
                    </div>
                  </div>
                  <div className="template-item">
                    <label>Button · loading / disabled</label>
                    <div style={{ display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap' }}>
                      <Button variant="primary" loading>Loading</Button>
                      <Button variant="primary" disabled>Disabled</Button>
                    </div>
                  </div>
                  <div className="template-item">
                    <label>IconButton · ghost / filled / glass</label>
                    <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                      <IconButton ariaLabel="Aizvērt" variant="ghost" icon={<span>&#10005;</span>} />
                      <IconButton ariaLabel="Meklēt" variant="filled" icon={<span>&#8981;</span>} />
                      <IconButton ariaLabel="Atpakaļ" variant="glass" icon={<span>&#8592;</span>} />
                    </div>
                  </div>
                </div>
              </div>

              {/* Buttons Section */}
              <div className="templates-section">
                <h3>Buttons Collection</h3>
                <div className="templates-grid buttons-grid">
                  <div className="template-item">
                    <label>CustomButton1</label>
                    <CustomButton1 label="Next" onClick={() => console.log('Button1 clicked')} />
                  </div>
                  
                  <div className="template-item">
                    <label>CustomButton2</label>
                    <CustomButton2 label="Search" onClick={() => console.log('Button2 clicked')} />
                  </div>
                  
                  <div className="template-item">
                    <label>CustomButton3</label>
                    <CustomButton3 label="Action" label2="->" onClick={() => console.log('Button3 clicked')} />
                  </div>
                  
                  <div className="template-item">
                    <label>CustomButton4</label>
                    <CustomButton4 label="Continue" onClick={() => console.log('Button4 clicked')} />
                  </div>
                  
                  <div className="template-item">
                    <label>CustomButton5</label>
                    <CustomButton5 label="Submit" onClick={() => console.log('Button5 clicked')} />
                  </div>
                  
                  <div className="template-item">
                    <label>CustomButton6</label>
                    <CustomButton6 label="Add Post" onClick={() => console.log('Button6 clicked')} />
                  </div>
                  
                  <div className="template-item">
                    <label>CustomButton7</label>
                    <CustomButton7 label="Save" onClick={() => console.log('Button7 clicked')}>
                      <FaArrowRight size={18}/>
                    </CustomButton7>
                  </div>
                  
                  <div className="template-item">
                    <label>CustomButton8</label>
                    <CustomButton8 label="Delete" onClick={() => console.log('Button8 clicked')}>
                      <FaArrowRight />
                    </CustomButton8>
                  </div>
                  
                  <div className="template-item">
                    <label>CustomButton9</label>
                    <CustomButton9 label="Upload" onClick={() => console.log('Button9 clicked')}>
                      <FaArrowRight />
                    </CustomButton9>
                  </div>
                  
                  <div className="template-item">
                    <label>CustomButton10</label>
                    <CustomButton10 label="Process" onClick={() => console.log('Button10 clicked')} />
                  </div>
                  
                  <div className="template-item">
                    <label>CustomButton10Ca</label>
                    <CustomButton10Ca label="Cancel" onClick={() => console.log('Button10Ca clicked')} />
                  </div>
                  
                  <div className="template-item">
                    <label>CustomButton10Co</label>
                    <CustomButton10Co label="Confirm" onClick={() => console.log('Button10Co clicked')} />
                  </div>
                  
                  <div className="template-item">
                    <label>CustomButton11</label>
                    <CustomButton11 label="Download" onClick={() => console.log('Button11 clicked')} />
                  </div>
                  
                  <div className="template-item">
                    <label>EventCardButton</label>
                    <EventCardButton label="View Event" onClick={() => console.log('EventButton clicked')} />
                  </div>
                </div>
              </div>

              {/* Input Components Section */}
              <div className="templates-section">
                <h3>Input Components</h3>
                <div className="templates-grid inputs-grid">
                  <div className="template-item">
                    <label>Checkbox1</label>
                    <Checkbox1
                      id="demo-checkbox"
                      label="Enable feature"
                      checked={switchValue}
                      onChange={(e) => setSwitchValue(e.target.checked)}
                    />
                  </div>

                  <div className="template-item">
                    <label>RadioButton1</label>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                      <RadioButton1
                        name="demo-radio"
                        label="Option 1"
                        value="option1"
                        checked={radioValue === 'option1'}
                        onChange={(e) => setRadioValue(e.target.value)}
                      />
                      <RadioButton1
                        name="demo-radio"
                        label="Option 2"
                        value="option2"
                        checked={radioValue === 'option2'}
                        onChange={(e) => setRadioValue(e.target.value)}
                      />
                      <RadioButton1
                        name="demo-radio"
                        label="Option 3"
                        value="option3"
                        checked={radioValue === 'option3'}
                        onChange={(e) => setRadioValue(e.target.value)}
                      />
                    </div>
                  </div>
                  
                  <div className="template-item">
                    <label>InputName1</label>
                    <InputName1 
                      id="demo-name" 
                      label="Username" 
                      value={inputValue}
                      onChange={(e) => setInputValue(e.target.value)}
                    />
                  </div>
                  
                  <div className="template-item">
                    <label>InputPassword1</label>
                    <InputPassword1 
                      id="demo-password" 
                      label="Password" 
                      value={passwordValue}
                      onChange={(e) => setPasswordValue(e.target.value)}
                    />
                  </div>
                  
                  <div className="template-item">
                    <label>CustomInputBox1</label>
                    <CustomInputBox1 
                      placeholder="Enter location" 
                      value={inputValue}
                      onChange={(e) => setInputValue(e.target.value)}
                    >
                      <FaCar />
                    </CustomInputBox1>
                  </div>
                  
                  <div className="template-item">
                    <label>CustomInputBox2</label>
                    <CustomInputBox2 
                      placeholder="Search..." 
                      value={inputValue}
                      onChange={(e) => setInputValue(e.target.value)}
                    />
                  </div>
                  
                  <div className="template-item wide">
                    <label>CustomTextArea</label>
                    <CustomTextArea 
                      placeholder="Enter your message here..." 
                      value={textareaValue}
                      onChange={(e) => setTextareaValue(e.target.value)}
                      maxLength={500}
                      rows={3}
                    />
                  </div>
                </div>
              </div>

              {/* Modern Components Section */}
              <div className="templates-section">
                <h3>Modern Components</h3>
                <div className="templates-grid modern-grid">
                  <div className="template-item">
                    <label>ModernDropdown</label>
                    <ModernDropdown 
                      label="Select Option"
                      value={dropdownValue}
                      options={['Option 1', 'Option 2', 'Option 3']}
                      onSelect={setDropdownValue}
                      icon={<FaUser />}
                      placeholder="Choose..."
                    />
                  </div>
                  
                  <div className="template-item">
                    <label>ModernSwitch</label>
                    <ModernSwitch 
                      label="Dark Mode"
                      checked={switchValue}
                      onChange={(e) => setSwitchValue(e.target.checked)}
                      leftIcon={<FaSun />}
                      rightIcon={<FaMoon />}
                    />
                  </div>
                  
                  <div className="template-item">
                    <label>AddStopButton</label>
                    <AddStopButton onClick={() => console.log('Add stop clicked')}>
                      <FaPlus />
                    </AddStopButton>
                  </div>
                  
                  <div className="template-item">
                    <label>NavigationContainer</label>
                    <NavigationContainer 
                      currentPage={currentButtonPage}
                      totalPages={3}
                      onPrevClick={() => setCurrentButtonPage(Math.max(0, currentButtonPage - 1))}
                      onNextClick={() => setCurrentButtonPage(Math.min(2, currentButtonPage + 1))}
                      showDots={true}
                    />
                  </div>
                  
                  <div className="template-item">
                    <label>EventCardDetail</label>
                    <EventCardDetail
                      icon={<FaCalendarAlt />}
                      text="March 15, 2024"
                    />
                  </div>

                  <div className="template-item">
                    <label>RefreshButton</label>
                    <RefreshButton
                      onClick={() => {
                        setRefreshLoading(true);
                        setTimeout(() => setRefreshLoading(false), 2000);
                        console.log('Refresh clicked');
                      }}
                      disabled={refreshLoading}
                      tooltip="Refresh data"
                      size="medium"
                    />
                  </div>

                  <div className="template-item">
                    <label>NavButton</label>
                    <div style={{ display: 'flex', gap: '10px' }}>
                      <NavButton
                        direction="prev"
                        onClick={() => console.log('Previous')}
                        title="Previous"
                      >
                        <FaChevronLeft />
                      </NavButton>
                      <NavButton
                        direction="next"
                        onClick={() => console.log('Next')}
                        title="Next"
                      >
                        <FaChevronRight />
                      </NavButton>
                    </div>
                  </div>

                  <div className="template-item wide">
                    <label>ModernDateTimePicker</label>
                    <ModernDateTimePicker
                      dateValue={dateValue}
                      timeValue={timeValue}
                      onDateChange={setDateValue}
                      onTimeChange={setTimeValue}
                    />
                  </div>

                  <div className="template-item wide">
                    <label>InlineThemeSelector</label>
                    <InlineThemeSelector />
                  </div>
                </div>
              </div>

              {/* Layout Components */}
              <div className="templates-section">
                <h3>Layout Components</h3>
                <div className="templates-grid layout-grid">
                  <div className="template-item wide">
                    <label>ModernFormSection</label>
                    <ModernFormSection
                      icon={<FaUser />}
                      title="User Information"
                    >
                      <p>Form content goes here...</p>
                    </ModernFormSection>
                  </div>

                  <div className="template-item wide">
                    <label>ModernCard</label>
                    <ModernCard>
                      <p>This is a modern card component with hover effects.</p>
                    </ModernCard>
                  </div>
                </div>
              </div>

              {/* Close Buttons Section */}
              <div className="templates-section">
                <h3>Close Buttons</h3>
                <div className="templates-grid buttons-grid">
                  <div className="template-item">
                    <label>CloseButton1 - Small</label>
                    <CloseButton1
                      size="small"
                      onClick={() => addToast({ type: 'info', title: 'Close button clicked!' })}
                    />
                  </div>

                  <div className="template-item">
                    <label>CloseButton1 - Medium</label>
                    <CloseButton1
                      size="medium"
                      onClick={() => addToast({ type: 'info', title: 'Close button clicked!' })}
                    />
                  </div>

                  <div className="template-item">
                    <label>CloseButton1 - Large</label>
                    <CloseButton1
                      size="large"
                      onClick={() => addToast({ type: 'info', title: 'Close button clicked!' })}
                    />
                  </div>

                  <div className="template-item">
                    <label>CloseButton2 - Small</label>
                    <CloseButton2
                      size="small"
                      onClick={() => addToast({ type: 'info', title: 'Close button clicked!' })}
                    />
                  </div>

                  <div className="template-item">
                    <label>CloseButton2 - Medium</label>
                    <CloseButton2
                      size="medium"
                      onClick={() => addToast({ type: 'info', title: 'Close button clicked!' })}
                    />
                  </div>

                  <div className="template-item">
                    <label>CloseButton2 - Large</label>
                    <CloseButton2
                      size="large"
                      onClick={() => addToast({ type: 'info', title: 'Close button clicked!' })}
                    />
                  </div>

                  <div className="template-item">
                    <label>CloseButton3 - Small</label>
                    <CloseButton3
                      size="small"
                      onClick={() => addToast({ type: 'info', title: 'Close button clicked!' })}
                    />
                  </div>

                  <div className="template-item">
                    <label>CloseButton3 - Medium</label>
                    <CloseButton3
                      size="medium"
                      onClick={() => addToast({ type: 'info', title: 'Close button clicked!' })}
                    />
                  </div>

                  <div className="template-item">
                    <label>CloseButton3 - Large</label>
                    <CloseButton3
                      size="large"
                      onClick={() => addToast({ type: 'info', title: 'Close button clicked!' })}
                    />
                  </div>
                </div>
              </div>

              {/* Back Buttons Section */}
              <div className="templates-section">
                <h3>Back Buttons - Mobile Friendly</h3>
                <div className="templates-grid buttons-grid">
                  <div className="template-item">
                    <label>BackButton1 - Small</label>
                    <BackButton1
                      size="small"
                      onClick={() => addToast({ type: 'info', title: 'Back button clicked!' })}
                    />
                  </div>

                  <div className="template-item">
                    <label>BackButton1 - Medium</label>
                    <BackButton1
                      size="medium"
                      onClick={() => addToast({ type: 'info', title: 'Back button clicked!' })}
                    />
                  </div>

                  <div className="template-item">
                    <label>BackButton1 - Large</label>
                    <BackButton1
                      size="large"
                      onClick={() => addToast({ type: 'info', title: 'Back button clicked!' })}
                    />
                  </div>

                  <div className="template-item">
                    <label>BackButton2 - Small</label>
                    <BackButton2
                      size="small"
                      onClick={() => addToast({ type: 'info', title: 'Back button clicked!' })}
                    />
                  </div>

                  <div className="template-item">
                    <label>BackButton2 - Medium</label>
                    <BackButton2
                      size="medium"
                      onClick={() => addToast({ type: 'info', title: 'Back button clicked!' })}
                    />
                  </div>

                  <div className="template-item">
                    <label>BackButton2 - Large</label>
                    <BackButton2
                      size="large"
                      onClick={() => addToast({ type: 'info', title: 'Back button clicked!' })}
                    />
                  </div>

                  <div className="template-item">
                    <label>BackButton2 - Custom Label</label>
                    <BackButton2
                      size="medium"
                      label="Go Back"
                      onClick={() => addToast({ type: 'info', title: 'Custom back clicked!' })}
                    />
                  </div>

                  <div className="template-item">
                    <label>BackButton3 - Small</label>
                    <BackButton3
                      size="small"
                      onClick={() => addToast({ type: 'info', title: 'Back button clicked!' })}
                    />
                  </div>

                  <div className="template-item">
                    <label>BackButton3 - Medium</label>
                    <BackButton3
                      size="medium"
                      onClick={() => addToast({ type: 'info', title: 'Back button clicked!' })}
                    />
                  </div>

                  <div className="template-item">
                    <label>BackButton3 - Large</label>
                    <BackButton3
                      size="large"
                      onClick={() => addToast({ type: 'info', title: 'Back button clicked!' })}
                    />
                  </div>

                  <div className="template-item">
                    <label>BackButton4 - Small</label>
                    <BackButton4
                      size="small"
                      onClick={() => addToast({ type: 'info', title: 'Back button clicked!' })}
                    />
                  </div>

                  <div className="template-item">
                    <label>BackButton4 - Medium</label>
                    <BackButton4
                      size="medium"
                      onClick={() => addToast({ type: 'info', title: 'Back button clicked!' })}
                    />
                  </div>

                  <div className="template-item">
                    <label>BackButton4 - Large</label>
                    <BackButton4
                      size="large"
                      onClick={() => addToast({ type: 'info', title: 'Back button clicked!' })}
                    />
                  </div>

                  <div className="template-item">
                    <label>BackButton4 - Custom Label</label>
                    <BackButton4
                      size="medium"
                      label="Return"
                      onClick={() => addToast({ type: 'info', title: 'Custom back clicked!' })}
                    />
                  </div>
                </div>
                <p style={{ fontSize: '12px', color: 'var(--main-color)', opacity: 0.7, marginTop: '12px', fontStyle: 'italic' }}>
                  BackButton1: Minimalist arrow with gradient | BackButton2: Pill-shaped with text | BackButton3: Circular glassmorphism | BackButton4: Flat modern with text
                </p>
              </div>

              {/* Search Buttons Section */}
              <div className="templates-section">
                <h3>Search Buttons - Icon Only</h3>
                <div className="templates-grid buttons-grid">
                  <div className="template-item">
                    <label>SearchButton1 - Small</label>
                    <SearchButton1
                      size="small"
                      onClick={() => addToast({ type: 'info', title: 'Search clicked!' })}
                    />
                  </div>

                  <div className="template-item">
                    <label>SearchButton1 - Medium</label>
                    <SearchButton1
                      size="medium"
                      onClick={() => addToast({ type: 'info', title: 'Search clicked!' })}
                    />
                  </div>

                  <div className="template-item">
                    <label>SearchButton1 - Large</label>
                    <SearchButton1
                      size="large"
                      onClick={() => addToast({ type: 'info', title: 'Search clicked!' })}
                    />
                  </div>

                  <div className="template-item">
                    <label>SearchButton2 - Small</label>
                    <SearchButton2
                      size="small"
                      onClick={() => addToast({ type: 'info', title: 'Search clicked!' })}
                    />
                  </div>

                  <div className="template-item">
                    <label>SearchButton2 - Medium</label>
                    <SearchButton2
                      size="medium"
                      onClick={() => addToast({ type: 'info', title: 'Search clicked!' })}
                    />
                  </div>

                  <div className="template-item">
                    <label>SearchButton2 - Large</label>
                    <SearchButton2
                      size="large"
                      onClick={() => addToast({ type: 'info', title: 'Search clicked!' })}
                    />
                  </div>
                </div>
              </div>

              {/* Search Buttons with Text Section */}
              <div className="templates-section">
                <h3>Search Buttons - With Text (i18n)</h3>
                <div className="templates-grid buttons-grid">
                  <div className="template-item">
                    <label>SearchButton3 - Small</label>
                    <SearchButton3
                      size="small"
                      onClick={() => addToast({ type: 'info', title: 'Search clicked!' })}
                    />
                  </div>

                  <div className="template-item">
                    <label>SearchButton3 - Medium</label>
                    <SearchButton3
                      size="medium"
                      onClick={() => addToast({ type: 'info', title: 'Search clicked!' })}
                    />
                  </div>

                  <div className="template-item">
                    <label>SearchButton3 - Large</label>
                    <SearchButton3
                      size="large"
                      onClick={() => addToast({ type: 'info', title: 'Search clicked!' })}
                    />
                  </div>

                  <div className="template-item">
                    <label>SearchButton4 - Small</label>
                    <SearchButton4
                      size="small"
                      onClick={() => addToast({ type: 'info', title: 'Search clicked!' })}
                    />
                  </div>

                  <div className="template-item">
                    <label>SearchButton4 - Medium</label>
                    <SearchButton4
                      size="medium"
                      onClick={() => addToast({ type: 'info', title: 'Search clicked!' })}
                    />
                  </div>

                  <div className="template-item">
                    <label>SearchButton4 - Large</label>
                    <SearchButton4
                      size="large"
                      onClick={() => addToast({ type: 'info', title: 'Search clicked!' })}
                    />
                  </div>
                </div>
                <p style={{ fontSize: '12px', color: 'var(--main-color)', opacity: 0.7, marginTop: '12px', fontStyle: 'italic' }}>
                  Text automatically translates to LV/EN/RU based on language selection
                </p>
              </div>

              {/* Chat Input Section */}
              <div className="templates-section">
                <h3>Chat Input (Mobile-Style)</h3>
                <div className="templates-grid inputs-grid">
                  <div className="template-item wide">
                    <label>ChatInput - Auto-expanding (max 3 lines)</label>
                    <ChatInput
                      placeholder="Type a message..."
                      value={chatMessage}
                      onChange={(e) => setChatMessage(e.target.value)}
                      onSend={() => {
                        addToast({ type: 'success', title: 'Message sent!', description: chatMessage });
                        console.log('Send message:', chatMessage);
                        setChatMessage('');
                      }}
                    />
                    <p style={{ fontSize: '12px', color: 'var(--main-color)', opacity: 0.7, marginTop: '8px' }}>
                      Press Enter to send, Shift+Enter for new line
                    </p>
                  </div>
                </div>
              </div>
            </ModernCard>
          )}
        </div>
      </div>
    </div>
  );
};

export default Developer;
